import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { File, X } from 'lucide-react';
import Modal from '../layout/ui/Modal';

const FileUpload = ({ label, onFileSelect, accept = '*', required = false }) => {
  const fileInputRef = useRef();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setFile(selected);
      onFileSelect(selected);
      const url = URL.createObjectURL(selected);
      setPreviewUrl(url);
    }
  };

  const resetFile = () => {
    setFile(null);
    setPreviewUrl(null);
    fileInputRef.current.value = '';
    onFileSelect(null);
  };

  const isImage = file?.type?.startsWith('image/');
  const isPDF = file?.type === 'application/pdf';

  return (
    <div className="space-y-1">
      <label className="font-medium text-gray-700 flex items-center">
        {label}
        {required && <span className="ml-1 text-darkred text-sm animate-pulse">*</span>}
      </label>

      <div className="flex items-center space-x-3">
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="border px-3 py-2 rounded w-full text-sm"
        />
        {file && (
          <button onClick={resetFile} className="text-darkred text-sm hover:text-red-600">
            <X /> Remove
          </button>
        )}
      </div>

      {file && (
        <div className="mt-2">
          {isImage ? (
            <div className="max-w-full overflow-hidden">
              <img
                src={previewUrl}
                alt="Preview"
                className="w-[595px] h-[842px] object-contain border shadow rounded"
              />
              <div className="mt-2">
                <button
                  onClick={() => setViewerOpen(true)}
                  className="text-primary hover:underline text-sm"
                >
                  Full Page View
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <File size={20} />
              <span>{file.name}</span>
              {isPDF && (
                <button
                  onClick={() => setViewerOpen(true)}
                  className="text-primary hover:underline ml-2"
                >
                  Full Page Preview
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Full Page Modal Viewer */}
      {viewerOpen && (
        <Modal
          isOpen={viewerOpen}
          onClose={() => setViewerOpen(false)}
          title={file?.name}
          className="max-w-none w-full h-screen p-0"
        >
          <div className="flex justify-center items-center h-[calc(100vh-60px)] bg-gray-100 overflow-auto">
            {isPDF ? (
              <iframe
                src={previewUrl}
                title="PDF Preview"
                className="w-[795px] h-[842px] bg-white border shadow"
              ></iframe>
            ) : (
              <img
                src={previewUrl}
                alt={file?.name || 'Preview'}
                className="w-[795px] h-[842px] object-contain bg-white border shadow"
              />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

FileUpload.propTypes = {
  label: PropTypes.string.isRequired,
  onFileSelect: PropTypes.func.isRequired,
  accept: PropTypes.string,
  required: PropTypes.bool
};

export default FileUpload;
